import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import { compare, type CrmStatsComparison } from '../api/stats'
import { executiveApi, type ExecutiveCompare, type ExecutiveKpiCompare, type KpiCompareCell } from '../api/executive'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Callout,
  Card,
  CardContent,
  DataTable,
  DataTableEmptyRow,
  FilterBar,
  LoadingState,
  Page,
  PageHeader,
  Select,
  Tabs,
  TextField,
} from '../components/ui'

/**
 * TASK-0146 / REQ-015-001 / REQ-015-002: "Confronto tra periodi" — replaces the `/executive`
 * PlaceholderPage ("Dashboard Direzionale") with a real period-comparison view: two date-range
 * pickers (periodo A, periodo B) and a table of Lead/Clienti/Contratti (totale)/Contratti
 * (valore) with Valore A / Valore B / Differenza assoluta / Differenza percentuale columns —
 * the exact structure REQ-015-002 specifies. Same no-client-side-route-gating pattern as
 * FunnelsPage.tsx/ImplementationStatusPage.tsx: the real gate is the backend's
 * [Authorize(PolicyNames.Authenticated)] on GET /api/stats/compare, a caller without a valid
 * session simply gets a plain error message from `load`'s catch below.
 */

const formatNum = (v: number): string => v.toLocaleString('it-IT')

const formatEur = (v: number): string =>
  '€ ' + v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Backend sends `null` (never a fake 0%/Infinity) when period A's value is 0 — render as an
 * em-dash, same convention FunnelsPage.tsx already uses for its own null rates. */
function formatPct(v: number | null): string {
  if (v === null) return '—'
  const sign = v > 0 ? '+' : ''
  return sign + v.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
}

function formatAbsDiff(v: number, isCurrency: boolean): string {
  const sign = v > 0 ? '+' : ''
  return sign + (isCurrency ? formatEur(v) : formatNum(v))
}

// REQ-015-001: StatsController.Compare's `conversionRate` metric is a 0..1 fraction (same
// convention FunnelsController's rates already use, see FunnelsPage.tsx's own formatPct), not a
// count or an amount — needs its own rendering so it doesn't show up as a bare "0,5" in the
// "Valore A"/"Valore B" columns.
const formatRate = (v: number): string => (v * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'

function formatRateDiff(v: number): string {
  const sign = v > 0 ? '+' : ''
  return sign + formatRate(v)
}

function diffClass(v: number): string {
  if (v > 0) return 'text-success'
  if (v < 0) return 'text-danger'
  return 'text-muted'
}

interface Row {
  key: string
  label: string
  valueA: number
  valueB: number
  absoluteDifference: number
  percentageDifference: number | null
  isCurrency: boolean
  // REQ-015-001: true only for `conversionRate` — mutually exclusive with isCurrency (a rate is
  // never rendered as either a plain count or a euro amount).
  isRate?: boolean
}

// ---------------------------------------------------------------------------------------------
// F-082 (TASK-0336, REQ-058-015, REQ-253-001) - tab "Per dimensione": mese A / mese B per settore, operatore,
// team, prodotto o agente. Stesse definizioni e stesso perimetro del cockpit Direzione (Manager); nessun importo.
// ---------------------------------------------------------------------------------------------

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'department', label: 'Settore' },
  { value: 'operator', label: 'Operatore' },
  { value: 'team', label: 'Team' },
  { value: 'product', label: 'Prodotto' },
  { value: 'agent', label: 'Agente' },
]

const COMPARE_MEASURES: { key: string; label: string }[] = [
  { key: 'inserted', label: 'Inserite' },
  { key: 'submitted', label: 'Inviate' },
  { key: 'activated', label: 'Attivate' },
  { key: 'rejected', label: 'Rifiutate' },
]

function monthOffset(offset: number): string {
  const d = new Date()
  const m = new Date(d.getFullYear(), d.getMonth() + offset, 1)
  return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`
}

const KPI_DIMENSIONS = DIMENSIONS.filter(d => ['department', 'operator', 'agent'].includes(d.value))
const eurCmp = (n: number) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

/** C-02 - mese su mese per settore, operatore e agente con ricavo maturato e incassato (solo lettura amministrativa). */
function KpiEstesiComparison({ monthA, monthB }: { monthA: string; monthB: string }) {
  const flagOn = useFeatureFlag('report.kpi_estesi')
  const [dimension, setDimension] = useState('department')
  const [data, setData] = useState<ExecutiveKpiCompare | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!flagOn || !monthA || !monthB) return
    let alive = true
    setError('')
    executiveApi.kpiCompare({}, monthA, monthB, dimension)
      .then(d => { if (alive) setData(d) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [flagOn, monthA, monthB, dimension])

  if (!flagOn) return null
  const cell = (c: KpiCompareCell | null | undefined, money: boolean) => {
    if (!c) return [<td key="a">—</td>, <td key="b">—</td>, <td key="d">—</td>]
    const f = money ? eurCmp : formatNum
    return [
      <td key="a" className="text-end md-num">{f(c.a)}</td>,
      <td key="b" className="text-end md-num">{f(c.b)}</td>,
      <td key="d" className={`text-end md-num ${diffClass(c.delta)}`}>{c.delta > 0 ? '+' : ''}{f(c.delta)}</td>,
    ]
  }
  const showMoney = data?.money === true
  const measures: { label: string; pick: (r: NonNullable<ExecutiveKpiCompare['rows']>[number]) => KpiCompareCell | null; money: boolean }[] = [
    { label: 'Inserite', pick: r => r.inserted, money: false },
    { label: 'Attivate', pick: r => r.activated, money: false },
    ...(showMoney ? [
      { label: 'Maturato', pick: (r: NonNullable<ExecutiveKpiCompare['rows']>[number]) => r.accrued, money: true },
      { label: 'Incassato', pick: (r: NonNullable<ExecutiveKpiCompare['rows']>[number]) => r.collected, money: true },
    ] : []),
  ]
  return (
    <Card>
      <CardContent>
        <Select label="Confronto esteso per" value={dimension} onChange={e => setDimension(e.target.value)}>
          {KPI_DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </Select>
        {error && <Callout tone="warning">Confronto esteso non disponibile. {error}</Callout>}
        {data?.enabled && data.rows && (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th rowSpan={2}>{KPI_DIMENSIONS.find(d => d.value === data.dimension)?.label}</th>
                {measures.map(m => <th key={m.label} colSpan={3} className="text-center">{m.label}</th>)}
              </tr>
              <tr>
                {measures.map(m => [
                  <th key={`${m.label}a`} className="text-end md-typescale-body-small">{data.a?.month}</th>,
                  <th key={`${m.label}b`} className="text-end md-typescale-body-small">{data.b?.month}</th>,
                  <th key={`${m.label}d`} className="text-end md-typescale-body-small">Differenza</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.key}>
                  <td className="md-typescale-emphasized-body-medium">{r.label}</td>
                  {measures.map(m => cell(m.pick(r), m.money))}
                </tr>
              ))}
              {data.rows.length === 0 && <DataTableEmptyRow colSpan={13} title="Nessun dato nei due mesi." />}
            </tbody>
          </DataTable>
        )}
      </CardContent>
    </Card>
  )
}

function DimensionComparison() {
  const [monthA, setMonthA] = useState(monthOffset(-1))
  const [monthB, setMonthB] = useState(monthOffset(0))
  const [dimension, setDimension] = useState('department')
  const [data, setData] = useState<ExecutiveCompare | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (a: string, b: string, dim: string) => {
    setLoading(true)
    setError('')
    try {
      setData(await executiveApi.compare({}, a, b, dim))
    } catch (e) {
      setData(null)
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(monthA, monthB, dimension)
    // Caricamento iniziale: le scelte successive si applicano col pulsante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cells = (c: { a: number; b: number; delta: number; deltaPct: number | null } | undefined) => {
    if (!c) return [<td key="a" className="text-end md-num">—</td>, <td key="b" className="text-end md-num">—</td>, <td key="d" className="text-end md-num">—</td>]
    return [
      <td key="a" className="text-end md-num">{formatNum(c.a)}</td>,
      <td key="b" className="text-end md-num">{formatNum(c.b)}</td>,
      <td key="d" className={`text-end md-num ${diffClass(c.delta)}`}>
        {c.delta > 0 ? '+' : ''}{formatNum(c.delta)}
        {c.deltaPct !== null && <span className="md-typescale-body-small ms-1">({formatPct(c.deltaPct * 100)})</span>}
      </td>,
    ]
  }

  return (
    <>
      <FilterBar
        onSubmit={() => { if (!loading && monthA && monthB) void load(monthA, monthB, dimension) }}
      >
        <TextField label="Mese A" type="month" value={monthA} onChange={e => setMonthA(e.target.value)} />
        <TextField label="Mese B" type="month" value={monthB} onChange={e => setMonthB(e.target.value)} />
        <Select label="Dimensione" value={dimension} onChange={e => setDimension(e.target.value)}>
          {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </Select>
      </FilterBar>

      {error && <Callout tone="warning">Confronto non disponibile. {error}</Callout>}
      {loading && !data && <LoadingState />}

      {data && (
        <Card>
          <CardContent>
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th rowSpan={2}>{DIMENSIONS.find(d => d.value === data.dimension)?.label ?? data.dimension}</th>
                    {COMPARE_MEASURES.map(m => <th key={m.key} colSpan={3} className="text-center">{m.label}</th>)}
                  </tr>
                  <tr>
                    {COMPARE_MEASURES.map(m => [
                      <th key={`${m.key}a`} className="text-end md-typescale-body-small">{data.a.month}</th>,
                      <th key={`${m.key}b`} className="text-end md-typescale-body-small">{data.b.month}</th>,
                      <th key={`${m.key}d`} className="text-end md-typescale-body-small">Differenza</th>,
                    ])}
                  </tr>
                </thead>
                <tbody>
                  <tr className="md-typescale-emphasized-body-medium">
                    <td>Totale</td>
                    {COMPARE_MEASURES.map(m => cells(data.totals[m.key]))}
                  </tr>
                  {data.rows.map(r => (
                    <tr key={r.key}>
                      <td className="md-typescale-emphasized-body-medium">{r.label}</td>
                      {COMPARE_MEASURES.map(m => cells(r.measures[m.key]))}
                    </tr>
                  ))}
                  {data.rows.length === 0 && <DataTableEmptyRow colSpan={13} title="Nessuna pratica nei due mesi." />}
                </tbody>
            </DataTable>
            <ul className="md-typescale-body-small text-muted mt-3 mb-0">
              {data.limitations.map(l => <li key={l}>{l}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}
      <KpiEstesiComparison monthA={monthA} monthB={monthB} />
    </>
  )
}

export function PeriodComparisonPage() {
  const [tab, setTab] = useState<'periods' | 'dimension'>('periods')
  const [fromA, setFromA] = useState('')
  const [toA, setToA] = useState('')
  const [fromB, setFromB] = useState('')
  const [toB, setToB] = useState('')
  const [data, setData] = useState<CrmStatsComparison | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (a1: string, a2: string, b1: string, b2: string) => {
    setLoading(true)
    setError('')
    try {
      const result = await compare(a1 || undefined, a2 || undefined, b1 || undefined, b2 || undefined)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(fromA, toA, fromB, toB)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function applyFilter() {
    void load(fromA, toA, fromB, toB)
  }

  function resetFilter() {
    setFromA('')
    setToA('')
    setFromB('')
    setToB('')
    void load('', '', '', '')
  }

  const rows: Row[] = data
    ? [
        {
          key: 'leadsTotal',
          label: 'Lead',
          valueA: data.metrics.find(m => m.metric === 'leadsTotal')?.valueA ?? 0,
          valueB: data.metrics.find(m => m.metric === 'leadsTotal')?.valueB ?? 0,
          absoluteDifference: data.metrics.find(m => m.metric === 'leadsTotal')?.absoluteDifference ?? 0,
          percentageDifference: data.metrics.find(m => m.metric === 'leadsTotal')?.percentageDifference ?? null,
          isCurrency: false,
        },
        {
          key: 'customersTotal',
          label: 'Clienti',
          valueA: data.metrics.find(m => m.metric === 'customersTotal')?.valueA ?? 0,
          valueB: data.metrics.find(m => m.metric === 'customersTotal')?.valueB ?? 0,
          absoluteDifference: data.metrics.find(m => m.metric === 'customersTotal')?.absoluteDifference ?? 0,
          percentageDifference: data.metrics.find(m => m.metric === 'customersTotal')?.percentageDifference ?? null,
          isCurrency: false,
        },
        {
          key: 'contractsTotal',
          label: 'Contratti (totale)',
          valueA: data.metrics.find(m => m.metric === 'contractsTotal')?.valueA ?? 0,
          valueB: data.metrics.find(m => m.metric === 'contractsTotal')?.valueB ?? 0,
          absoluteDifference: data.metrics.find(m => m.metric === 'contractsTotal')?.absoluteDifference ?? 0,
          percentageDifference: data.metrics.find(m => m.metric === 'contractsTotal')?.percentageDifference ?? null,
          isCurrency: false,
        },
        // REQ-015-001: activationsTotal/cancellationsTotal/conversionRate are counts and a ratio
        // (never amounts), so — same as the 3 rows above — they are unconditional: StatsController.
        // Compare always includes them in `metrics`, no CanSeeRealAmounts gate applies.
        {
          key: 'activationsTotal',
          label: 'Attivazioni',
          valueA: data.metrics.find(m => m.metric === 'activationsTotal')?.valueA ?? 0,
          valueB: data.metrics.find(m => m.metric === 'activationsTotal')?.valueB ?? 0,
          absoluteDifference: data.metrics.find(m => m.metric === 'activationsTotal')?.absoluteDifference ?? 0,
          percentageDifference: data.metrics.find(m => m.metric === 'activationsTotal')?.percentageDifference ?? null,
          isCurrency: false,
        },
        {
          key: 'cancellationsTotal',
          label: 'Storni',
          valueA: data.metrics.find(m => m.metric === 'cancellationsTotal')?.valueA ?? 0,
          valueB: data.metrics.find(m => m.metric === 'cancellationsTotal')?.valueB ?? 0,
          absoluteDifference: data.metrics.find(m => m.metric === 'cancellationsTotal')?.absoluteDifference ?? 0,
          percentageDifference: data.metrics.find(m => m.metric === 'cancellationsTotal')?.percentageDifference ?? null,
          isCurrency: false,
        },
        {
          key: 'conversionRate',
          label: 'Conversioni',
          valueA: data.metrics.find(m => m.metric === 'conversionRate')?.valueA ?? 0,
          valueB: data.metrics.find(m => m.metric === 'conversionRate')?.valueB ?? 0,
          absoluteDifference: data.metrics.find(m => m.metric === 'conversionRate')?.absoluteDifference ?? 0,
          percentageDifference: data.metrics.find(m => m.metric === 'conversionRate')?.percentageDifference ?? null,
          isCurrency: false,
          isRate: true,
        },
        // TASK-0202 / REQ-058-014: same conditional-append treatment as the "Commissioni" row
        // below, for the same reason — StatsController.Compare now omits `contractsTotalValue`
        // from `metrics` entirely (not a zeroed value) for a caller whose CanSeeRealAmounts gate
        // denies them, so the previous unconditional row with its `?? 0` fallback would have
        // rendered a fabricated "€ 0,00" to exactly that denied caller.
        ...(data.metrics.some(m => m.metric === 'contractsTotalValue')
          ? [
              {
                key: 'contractsTotalValue',
                label: 'Contratti (valore)',
                valueA: data.metrics.find(m => m.metric === 'contractsTotalValue')?.valueA ?? 0,
                valueB: data.metrics.find(m => m.metric === 'contractsTotalValue')?.valueB ?? 0,
                absoluteDifference: data.metrics.find(m => m.metric === 'contractsTotalValue')?.absoluteDifference ?? 0,
                percentageDifference: data.metrics.find(m => m.metric === 'contractsTotalValue')?.percentageDifference ?? null,
                isCurrency: true,
              },
            ]
          : []),
        // TASK-0194 / REQ-015-001: unlike the 6 unconditional rows above, this one is added ONLY when the
        // backend actually included `commissionsTotal` in `metrics` — StatsController.Compare
        // omits that metric entirely (not a zeroed value) for a caller whose CanSeeRealAmounts
        // gate denies them, and an unconditional `?? 0` fallback here would render a fake
        // "€ 0,00" to exactly that denied caller, the same "number that looks real but isn't"
        // this codebase avoids everywhere else (see PeriodComparisonPage's own `formatPct`
        // null-handling above, and StatsController's remarks).
        ...(data.metrics.some(m => m.metric === 'commissionsTotal')
          ? [
              {
                key: 'commissionsTotal',
                label: 'Commissioni',
                valueA: data.metrics.find(m => m.metric === 'commissionsTotal')?.valueA ?? 0,
                valueB: data.metrics.find(m => m.metric === 'commissionsTotal')?.valueB ?? 0,
                absoluteDifference: data.metrics.find(m => m.metric === 'commissionsTotal')?.absoluteDifference ?? 0,
                percentageDifference: data.metrics.find(m => m.metric === 'commissionsTotal')?.percentageDifference ?? null,
                isCurrency: true,
              },
            ]
          : []),
        // REQ-015-001: `collectionsTotal` sums Contract.CollectedAmount — real money, same as
        // contractsTotalValue/commissionsTotal above — so it gets the identical conditional-append
        // treatment: present only when StatsController.Compare's CanSeeRealAmounts gate allows it,
        // never a fabricated "€ 0,00" for a denied caller.
        ...(data.metrics.some(m => m.metric === 'collectionsTotal')
          ? [
              {
                key: 'collectionsTotal',
                label: 'Incassi',
                valueA: data.metrics.find(m => m.metric === 'collectionsTotal')?.valueA ?? 0,
                valueB: data.metrics.find(m => m.metric === 'collectionsTotal')?.valueB ?? 0,
                absoluteDifference: data.metrics.find(m => m.metric === 'collectionsTotal')?.absoluteDifference ?? 0,
                percentageDifference: data.metrics.find(m => m.metric === 'collectionsTotal')?.percentageDifference ?? null,
                isCurrency: true,
              },
            ]
          : []),
      ]
    : []

  return (
    <Page>
      <PageHeader title="Confronto periodi" icon="speed" actions={<ReportExportButtons title="Confronto tra periodi" />} />
      <Tabs
        aria-label="Tipo di confronto"
        value={tab}
        onChange={id => setTab(id as 'periods' | 'dimension')}
        items={[
          { id: 'periods', label: 'Per periodo' },
          { id: 'dimension', label: 'Per dimensione' },
        ]}
      />

      {tab === 'dimension' ? (
        <DimensionComparison />
      ) : (
      <>
      <FilterBar onSubmit={applyFilter} onReset={resetFilter} busy={loading}>
        <TextField label="Periodo A — Dal" type="date" value={fromA} onChange={e => setFromA(e.target.value)} />
        <TextField label="Periodo A — Al" type="date" value={toA} onChange={e => setToA(e.target.value)} />
        <TextField label="Periodo B — Dal" type="date" value={fromB} onChange={e => setFromB(e.target.value)} />
        <TextField label="Periodo B — Al" type="date" value={toB} onChange={e => setToB(e.target.value)} />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading && !data && <LoadingState />}

      {data && (
        <Card>
          <CardContent>
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Indicatore</th>
                    <th className="text-end">Valore A</th>
                    <th className="text-end">Valore B</th>
                    <th className="text-end">Differenza assoluta</th>
                    <th className="text-end">Differenza percentuale</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.key}>
                      <td className="md-typescale-emphasized-body-medium">{row.label}</td>
                      <td className="text-end md-num">{row.isRate ? formatRate(row.valueA) : row.isCurrency ? formatEur(row.valueA) : formatNum(row.valueA)}</td>
                      <td className="text-end md-num">{row.isRate ? formatRate(row.valueB) : row.isCurrency ? formatEur(row.valueB) : formatNum(row.valueB)}</td>
                      <td className={`text-end md-num ${diffClass(row.absoluteDifference)}`}>
                        {row.isRate ? formatRateDiff(row.absoluteDifference) : formatAbsDiff(row.absoluteDifference, row.isCurrency)}
                      </td>
                      <td className={`text-end md-num ${row.percentageDifference === null ? 'text-muted' : diffClass(row.percentageDifference)}`}>
                        {formatPct(row.percentageDifference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
            </DataTable>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </Page>
  )
}
